import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { freelancerService } from '@/lib/api/services/freelancer.service';
import { FreelancerCard } from '@/components/freelancer/freelancer-card';
import { GlobalNavigation } from '@/components/global-navigation';
import { LandingFooter } from '@/components/landing/landing-footer';
import { SEOHead } from '@/components/seo/SEOHead';
import { SKILL_PAGES, FEATURED_SKILL_SLUGS } from '@/config/skill-pages';
import { Users, ArrowRight } from 'lucide-react';
import { SignUpButton, useUser } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';

export default function SkillLandingPage() {
  const { skillSlug } = useParams<{ skillSlug: string }>();
  const { isSignedIn } = useUser();
  const config = skillSlug ? SKILL_PAGES[skillSlug] : undefined;

  const { data: experts = [], isLoading } = useQuery({
    queryKey: ['skill-experts', skillSlug],
    queryFn: () => freelancerService.getPublicProfilesBySkill(config!.skills),
    enabled: !!config,
    staleTime: 5 * 60 * 1000,
  });

  if (!config) {
    return (
      <>
        <GlobalNavigation />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-slate-900 mb-2">Category not found</h1>
            <p className="text-slate-500 mb-6">Browse our expert categories below.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {FEATURED_SKILL_SLUGS.map((slug) => (
                <Link
                  key={slug}
                  to={`/experts/${slug}`}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-700 hover:border-blue-300 hover:text-blue-600 transition-colors"
                >
                  {SKILL_PAGES[slug].title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  const relatedSlugs = Object.keys(SKILL_PAGES).filter((s) => s !== skillSlug).slice(0, 6);

  return (
    <>
      <SEOHead
        title={config.title}
        description={config.description}
        canonicalUrl={`https://aiexpertnetwork.com/experts/${skillSlug}`}
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'CollectionPage',
              name: config.title,
              description: config.description,
              url: `https://aiexpertnetwork.com/experts/${skillSlug}`,
              provider: {
                '@type': 'Organization',
                name: 'AI Expert Network',
                url: 'https://aiexpertnetwork.com',
              },
            },
            {
              '@type': 'FAQPage',
              mainEntity: config.faqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            },
          ],
        }}
      />
      <GlobalNavigation />
      <div className="min-h-screen bg-slate-50">
        {/* Hero */}
        <div
          className="py-10 sm:py-14 lg:py-18 px-4 sm:px-6 lg:px-8"
          style={{
            background:
              'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(248, 250, 252, 1) 100%)',
          }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              {config.heading}
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-4">
              {config.description}
            </p>
            {/* Quick answer block for GEO/AI search citation */}
            <p className="text-sm text-slate-500 max-w-xl mx-auto mb-6 bg-white/60 rounded-xl px-4 py-3 border border-slate-200/50">
              {config.quickAnswer}
            </p>
            {!isLoading && experts.length > 0 && (
              <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span>{experts.length} expert{experts.length !== 1 ? 's' : ''} available</span>
                </div>
                <span className="text-slate-300">|</span>
                <span>Last updated {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              </div>
            )}
          </div>
        </div>

        {/* Experts Grid */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-3xl border border-slate-200 p-6 h-[354px] animate-pulse"
                >
                  <div className="flex gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-slate-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-3/4" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="space-y-2 mt-6">
                    <div className="h-3 bg-slate-200 rounded w-full" />
                    <div className="h-3 bg-slate-200 rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : experts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {experts.map((expert: any) => (
                <FreelancerCard key={expert.id} freelancer={expert} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg mb-4">
                We're currently growing our {config.title.toLowerCase()} roster.
              </p>
              <p className="text-slate-400">
                Check back soon or{' '}
                <Link to="/sign-up" className="text-blue-600 hover:underline">
                  sign up
                </Link>{' '}
                to be notified when new experts join.
              </p>
            </div>
          )}
        </div>

        {/* CTA for non-authenticated users */}
        {!isSignedIn && experts.length > 0 && (
          <div className="bg-white border-t border-b border-slate-200 py-12">
            <div className="max-w-2xl mx-auto text-center px-4">
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">
                Ready to work with these experts?
              </h2>
              <p className="text-slate-600 mb-6">
                Sign up to browse full profiles, start conversations, and hire the right expert for your project.
              </p>
              <SignUpButton mode="modal">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8">
                  Get Started <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </SignUpButton>
            </div>
          </div>
        )}

        {/* FAQ Section (GEO-optimized for AI search citation) */}
        {config.faqs.length > 0 && (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {config.faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.question}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Related Categories (internal linking for SEO) */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Browse More Expert Categories</h2>
          <div className="flex flex-wrap gap-2">
            {relatedSlugs.map((slug) => (
              <Link
                key={slug}
                to={`/experts/${slug}`}
                className="px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-700 hover:border-blue-300 hover:text-blue-600 transition-colors"
              >
                {SKILL_PAGES[slug].title}
              </Link>
            ))}
          </div>
        </div>
      </div>
      <LandingFooter />
    </>
  );
}
